import { REQUEST_LOADING_STATES } from '../../../helpers/constant';
import { createSlice } from '@reduxjs/toolkit';
import { TrackingLinkDto } from '../../../helpers/types';
import { createTrackingLinkAction, deleteTrackingLinkAction, fetchTrackingLinks, updateTrackingLinkAction } from '../thunks/trackingLinksThunks';

interface TrackingLinksCache {
  creatorId: string;
  trackingLinks: TrackingLinkDto[];
  ttl?: number;
  llt?: number;
}

interface TrackingLinkState {
  loading: string;
  error: string | null;
  TrackingLinksByCreator: TrackingLinksCache[];
  trackingLinksByKey: Array<{
    key: string;
    items: TrackingLinkDto[];
    ttl?: number;
    llt?: number;
  }>;
  loadingByKey: Record<string, string>;
}

const initialState: TrackingLinkState = {
  loading: REQUEST_LOADING_STATES.IDLE,
  error: null,
  TrackingLinksByCreator: [],
  trackingLinksByKey: [],
  loadingByKey: {},
};

const trackingLinkSlice = createSlice({
  name: 'trackingLink',
  initialState,
  reducers: {
    clearTrackingLinksCache: (state) => {
      state.trackingLinksByKey = [];
      state.loadingByKey = {};
    },
    removeTrackingLinkFromCache: (state, action) => {
      const { creatorId, id } = action.payload as { creatorId: string; id: string };
      const keyPrefix = `${creatorId}__`;
      state.trackingLinksByKey = state.trackingLinksByKey.map((entry) => {
        if (!entry.key.startsWith(keyPrefix)) return entry;
        const filtered = entry.items.filter((it: TrackingLinkDto) => it._id !== id);
        return { ...entry, items: filtered } as typeof entry;
      });
    },
    setTrackingLinksForKey: (state, action) => {
      const { creatorId, status, items } = action.payload as { creatorId: string; status: string; items: TrackingLinkDto[] };
      const key = `${creatorId}__${status}`;
      const idx = state.trackingLinksByKey.findIndex((e) => e.key === key);
      if (idx >= 0) {
        state.trackingLinksByKey[idx].items = items as any;
        const stampAll = status === 'active';
        (state.trackingLinksByKey[idx].items as any[]).forEach((it: any, i: number) => {
          if (stampAll) it.globalOrder = i;
          else it.categoryOrder = i;
        });
        state.trackingLinksByKey[idx].llt = Date.now();
      } else {
        const stampAll = status === 'active';
        const stamped = (items as any[]).map((it: any, i: number) => ({
          ...it,
          ...(stampAll ? { globalOrder: i } : { categoryOrder: i }),
        }));
        state.trackingLinksByKey.push({
          key,
          items: stamped as any,
          ttl: 5,
          llt: Date.now(),
        } as any);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrackingLinks.pending, (state) => {
        state.loading = REQUEST_LOADING_STATES.PENDING;
        state.error = null;
      })
      .addCase(fetchTrackingLinks.fulfilled, (state, action) => {
        const { creatorId, trackingLinks, key } = action.payload;

        state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
        state.loadingByKey[key] = REQUEST_LOADING_STATES.SUCCEEDED;

        const idxByKey = state.trackingLinksByKey.findIndex((e) => e.key === key);
        if (idxByKey >= 0) {
          state.trackingLinksByKey[idxByKey].items = trackingLinks;
          state.trackingLinksByKey[idxByKey].llt = Date.now();
        } else {
          state.trackingLinksByKey.push({
            key,
            items: trackingLinks,
            ttl: 5,
            llt: Date.now(),
          });
        }

        const idxByCreator = state.TrackingLinksByCreator.findIndex((c) => c.creatorId === creatorId);
        if (idxByCreator >= 0) {
          state.TrackingLinksByCreator[idxByCreator].trackingLinks = trackingLinks;
          state.TrackingLinksByCreator[idxByCreator].llt = Date.now();
        } else {
          state.TrackingLinksByCreator.push({
            creatorId,
            trackingLinks,
            ttl: 5,
            llt: Date.now(),
          });
        }
      })
      .addCase(fetchTrackingLinks.rejected, (state, action) => {
        state.loading = REQUEST_LOADING_STATES.FAILED;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to fetch tracking links';
      })
      .addCase(createTrackingLinkAction.fulfilled, (state, action) => {
        const { creatorId, trackingLink } = action.payload;
        const idx = state.TrackingLinksByCreator.findIndex((c) => c.creatorId === creatorId);
        if (idx >= 0) {
          state.TrackingLinksByCreator[idx].trackingLinks = [...state.TrackingLinksByCreator[idx].trackingLinks, trackingLink];
          state.TrackingLinksByCreator[idx].llt = Date.now();
        } else {
          state.TrackingLinksByCreator.push({ creatorId, trackingLinks: [trackingLink], ttl: 5, llt: Date.now() });
        }
      })
      .addCase(updateTrackingLinkAction.fulfilled, (state, action) => {
        const { id, name } = action.payload;
        state.TrackingLinksByCreator = state.TrackingLinksByCreator.map((entry) => ({
          ...entry,
          trackingLinks: entry.trackingLinks.map((c: TrackingLinkDto) => (c._id === id ? { ...c, name } : c)),
        }));
      })
      .addCase(deleteTrackingLinkAction.fulfilled, (state, action) => {
        const { id } = action.payload;
        state.TrackingLinksByCreator = state.TrackingLinksByCreator.map((entry) => ({
          ...entry,
          trackingLinks: entry.trackingLinks.filter((c) => c._id !== id),
        }));
      })
  },
});

export const { clearTrackingLinksCache, removeTrackingLinkFromCache, setTrackingLinksForKey } = trackingLinkSlice.actions;
export default trackingLinkSlice.reducer;
